import { useEffect, useMemo, useState } from "react";
import ResourcesHero from "./resources/ResourcesHero";
import ResourcesSection from "./resources/ResourcesSection";
import { sections, articlesBySection, tools, CATEGORY_TO_SECTION } from "./resources/resourcesData";
import ToolsSection from "./resources/ToolsSection";
import { base44 } from "@/api/base44Client";

export default function Resources() {
  const [searchQuery, setSearchQuery] = useState("");
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewCountsBySlug, setViewCountsBySlug] = useState({});

  useEffect(() => {
    base44.entities.ResourcePost.filter({ indexable: true }, "-publish_date")
      .then(setGeneratedPosts)
      .catch(() => setGeneratedPosts([]));
  }, []);

  useEffect(() => {
    base44.auth.me().then(u => { if (u?.role === "admin") setIsAdmin(true); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    base44.entities.ResourceEvent.filter({ event_type: "session" }, "-created_date", 5000)
      .then(events => {
        const counts = {};
        (events || []).forEach(e => {
          if (!e.page) return;
          // Resource article views are stored as "resource:{slug}" — normalise to the bare slug
          const key = e.page.startsWith("resource:") ? e.page.slice(9) : e.page;
          counts[key] = (counts[key] || 0) + 1;
        });
        setViewCountsBySlug(counts);
      })
      .catch(() => {});
  }, [isAdmin]);

  // Lookup of publish date by slug (from stored ResourcePost records)
  const publishDateBySlug = useMemo(() => {
    const m = {};
    generatedPosts.forEach(p => { if (p.slug) m[p.slug] = p.publish_date; });
    return m;
  }, [generatedPosts]);

  const slugOf = (href) => (href || "").split("/").filter(Boolean).pop();

  // Merge AI-generated posts into the matching static section, newest first.
  // Attach a publish date (for the "x ago" label) and view count to every article.
  const combinedArticlesBySection = useMemo(() => {
    const combined = {};
    for (const s of sections) {
      combined[s.id] = (articlesBySection[s.id] || []).map(a => {
        const slug = slugOf(a.href);
        return {
          ...a,
          publishDate: a.publishDate || publishDateBySlug[slug] || null,
          viewCount: viewCountsBySlug[slug] || 0,
        };
      });
    }
    generatedPosts.forEach((post) => {
      const sectionId = CATEGORY_TO_SECTION[post.category];
      if (!combined[sectionId]) return;
      combined[sectionId].unshift({
        tag: post.tag || "Guide",
        title: post.title,
        desc: post.excerpt || post.meta_description,
        img: post.img,
        href: `/resources/${post.slug}`,
        publishDate: post.publish_date,
        viewCount: viewCountsBySlug[post.slug] || 0,
      });
    });
    // Sort every section soonest (newest) to oldest; undated cards stay last.
    for (const id of Object.keys(combined)) {
      combined[id].sort((a, b) => {
        const da = a.publishDate ? new Date(a.publishDate).getTime() : -Infinity;
        const db = b.publishDate ? new Date(b.publishDate).getTime() : -Infinity;
        return db - da;
      });
    }
    return combined;
  }, [generatedPosts, publishDateBySlug, viewCountsBySlug]);

  useEffect(() => {
    document.title = "Airbnb Landlord Guides & Resources | Truestays";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Free guides for UK landlords on short-let income, management, pricing and compliance. 35,000+ stays managed. Find out how.");
    setMeta("robots", "index, follow");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://www.truestays.co.uk/resources";

    // Scroll to section if arriving via hash (e.g. from mega menu)
    const hash = window.location.hash;
    if (hash) {
      const id = hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 68;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 200);
    }
  }, []);

  // Filter articles based on search query
  const filterArticles = (articles) => {
    if (!searchQuery.trim()) return articles;
    const query = searchQuery.toLowerCase().trim();
    return articles.filter(article => 
      article.title.toLowerCase().includes(query) ||
      article.desc.toLowerCase().includes(query) ||
      article.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  };

  // Filter tools based on search query
  const filterTools = (tools) => {
    if (!searchQuery.trim()) return tools;
    const query = searchQuery.toLowerCase().trim();
    return tools.filter(tool => 
      tool.title.toLowerCase().includes(query) ||
      tool.desc.toLowerCase().includes(query)
    );
  };

  const hasSearchResults = searchQuery.trim().length > 0;

  return (
    <>
      <ResourcesHero sections={sections} onSearch={setSearchQuery} searchQuery={searchQuery} />
      {!hasSearchResults && <ToolsSection tools={tools} />}
      {hasSearchResults ? (
        // Show search results
        <section style={{ backgroundColor: "#fff", padding: "64px clamp(20px, 5vw, 80px)" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", marginBottom: 24, marginTop: 0 }}>
              {Object.values(combinedArticlesBySection).flat().filter(a => 
                a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                a.desc.toLowerCase().includes(searchQuery.toLowerCase())
              ).length + filterTools(tools).length} results for "{searchQuery}"
            </p>
            {/* Tools results */}
            {filterTools(tools).length > 0 && (
              <div style={{ marginBottom: 48 }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 16, marginTop: 0 }}>Tools & Calculators</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
                  {filterTools(tools).map((tool) => (
                    <a
                      key={tool.slug}
                      href={`/resources/tools/${tool.slug}`}
                      style={{ background: "#fff", border: "1px solid #EBEBEB", borderRadius: 12, padding: "20px", textDecoration: "none", transition: "box-shadow 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                    >
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#888", margin: "0 0 8px" }}>{tool.tag}</p>
                      <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: "#1A1A1A", margin: "0 0 8px", letterSpacing: "-0.02em" }}>{tool.title}</h3>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#666", lineHeight: 1.5, margin: 0 }}>{tool.desc}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {/* Article results */}
            {Object.entries(combinedArticlesBySection).map(([sectionId, articles]) => {
              const filtered = filterArticles(articles);
              if (filtered.length === 0) return null;
              return (
                <ResourcesSection
                  key={sectionId}
                  id={sectionId}
                  label={sections.find(s => s.id === sectionId)?.label || sectionId}
                  desc=""
                  articles={filtered}
                  alt={false}
                  isAdmin={isAdmin}
                />
              );
            })}
          </div>
        </section>
      ) : (
        // Show all sections (skip any with no articles yet)
        sections
          .filter((s) => (combinedArticlesBySection[s.id] || []).length > 0)
          .map((s, i) => (
            <ResourcesSection
              key={s.id}
              id={s.id}
              label={s.label}
              desc={s.desc}
              articles={combinedArticlesBySection[s.id]}
              alt={i % 2 === 1}
              isAdmin={isAdmin}
            />
          ))
      )}
    </>
  );
}